import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { Modal, Form, Button, InputGroup } from "react-bootstrap";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { Popover } from "react-tiny-popover";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import CurrencyCustomInput from "../../utils/CurrencyCustomInput";
import ExpensesSelector from "../../utils/ExpensesSelector";
import { useBackendUrl } from "../../../utils/hooks";
import ConfirmDialog from "../../ConfirmDialogue";
import ModalLoader from "../../utils/ModalLoader";

export default function RefundAtcModal({
  show,
  onHide,
  batch,
  invoiceNo,
  vendor,
}) {
  const backendUrl = useBackendUrl();
  const queryClient = useQueryClient();
  const [showJournalPopover, setShowJournalPopover] = useState(false);

  const batchValue = useMemo(() => {
    const qty = Number(batch?.originalQuantity ?? batch?.Quantity) || 0;
    const unitCost = Number(batch?.UnitCost) || 0;
    return qty * unitCost;
  }, [batch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      AccountID: "",
      Description: "",
      Type: "",
      amount: batchValue,
    },
    validate: values => {
      const errors = {};
      if (!values.AccountID) errors.AccountID = "Select account";
      if (!values.amount || Number(values.amount) <= 0)
        errors.amount = "Enter a valid amount";
      return errors;
    },
    onSubmit: async values => {
      const isConfirmed = await ConfirmDialog({
        title: "Confirm refund",
        description: `This will refund ${Number(values.amount).toLocaleString(
          "en-NG",
          { style: "currency", currency: "NGN", maximumFractionDigits: 2 }
        )} for ${batch?.ATC_Code || "this batch"} to ${vendor?.CompanyName ||
          "the vendor"}, posting to ${values.Description} (${
          values.AccountID
        }).`,
      });
      if (!isConfirmed) return;

      refundMutation.mutate({
        InvoiceNo: invoiceNo,
        Trans_ID: batch?.Trans_ID,
        ATC_Code: batch?.ATC_Code,
        Bar_Code: batch?.Bar_Code,
        Vendor_ID: vendor?.Vendor_ID,
        Amount: Number(values.amount),
        AccountID: values.AccountID,
        AccountDescription: values.Description,
        AccountType: values.Type,
      });
    },
  });

  const handleSelectedAccount = account => {
    formik.setFieldValue("AccountID", account?.AccountID || "");
    formik.setFieldValue("Description", account?.Description || "");
    formik.setFieldValue("Type", account?.Type || "");
    setShowJournalPopover(false);
  };

  const refundAtcBatch = async payload => {
    const response = await fetch(`${backendUrl}/api/atc/batches-refund`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.message || "Unable to refund ATC batch");
    }

    return response.json();
  };

  const refundMutation = useMutation(payload => refundAtcBatch(payload), {
    onSuccess: result => {
      toast.success(result?.message || "ATC batch refunded successfully");
      queryClient.invalidateQueries(["ATC_BATCHES_INVOICE", invoiceNo]);
      onHide();
    },
    onError: error => {
      toast.error(
        error?.message || "Unable to refund ATC batch, please try again"
      );
    },
  });

  useEffect(() => {
    if (!show) {
      formik.resetForm();
      setShowJournalPopover(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        size="md"
        animation={false}
        autoFocus={false}
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            Refund Batch{batch?.ATC_Code ? ` - ${batch.ATC_Code}` : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
            <Form.Group className="mb-3">
              <Form.Label>Vendor</Form.Label>
              <Form.Control
                readOnly
                value={vendor?.CompanyName || "—"}
                className="bg-lighter"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Value of Batch (Unit Cost x Qty)</Form.Label>
              <CurrencyCustomInput value={batchValue} readOnly />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Cancellation Account</Form.Label>
              <InputGroup hasValidation>
                <Form.Control
                  placeholder="Select Cancellation Account"
                  value={
                    formik.values.AccountID
                      ? `${formik.values.AccountID} / ${
                          formik.values.Description
                        } / ${formik.values.Type}`
                      : ""
                  }
                  onChange={() => {}}
                  readOnly
                  isInvalid={
                    formik.touched.AccountID && !!formik.errors.AccountID
                  }
                />
                <Popover
                  isOpen={showJournalPopover}
                  onClickOutside={() => setShowJournalPopover(false)}
                  content={() => (
                    <ExpensesSelector
                      handleSelectedExpense={handleSelectedAccount}
                      usage="chart of accounts"
                      onHide={() => setShowJournalPopover(false)}
                    />
                  )}
                  position="bottom"
                >
                  <InputGroup.Text onClick={() => setShowJournalPopover(true)}>
                    <MagnifyIcon />
                  </InputGroup.Text>
                </Popover>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.AccountID}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <CurrencyCustomInput
                name="amount"
                value={formik.values.amount}
                onValueChange={(value, name) => {
                  formik.setFieldValue(name, value);
                }}
                onBlur={() => formik.setFieldTouched("amount", true)}
                isInvalid={formik.touched.amount && !!formik.errors.amount}
                disabled
              />
              {formik.touched.amount && formik.errors.amount ? (
                <div className="invalid-feedback d-block">
                  {formik.errors.amount}
                </div>
              ) : null}
            </Form.Group>

            <div className="d-flex gap-2 justify-content-end">
              <Button
                variant="white"
                type="button"
                onClick={onHide}
                disabled={refundMutation.isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={refundMutation.isLoading}>
                {refundMutation.isLoading ? "Processing..." : "Refund"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <ModalLoader show={refundMutation.isLoading} />
    </>
  );
}
